import { useState } from 'react';
import { t } from '@app/i18n';
import { Controller, useForm } from 'react-hook-form';
import { Field, IconButton, LoadingButton, TextInput } from '@zarya/ui';
import { loginDefaults, type LoginFormValues } from './LoginModel';
import { useLoginValidation } from './useFormValidation';
import s from './Login.module.css';

export interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => void;
  /** `true` enquanto o `login` está em voo. */
  pending?: boolean;
  /** Backend fora: nada a tentar até voltar (FR-011). */
  bloqueado?: boolean;
}

/**
 * Formulário de T1 (FR-001, FR-004, FR-007).
 *
 * **Entrar** só habilita com e-mail válido e senha de 8+ caracteres, validados
 * localmente; o servidor decide o resto e nunca diz qual dos dois campos falhou
 * (FR-002), então o erro da chamada mora no banner da página, não aqui.
 */
export function LoginForm({ onSubmit, pending = false, bloqueado = false }: LoginFormProps) {
  const [verSenha, setVerSenha] = useState(false);
  const { control, handleSubmit, formState } = useForm<LoginFormValues>({
    defaultValues: loginDefaults,
    ...useLoginValidation(),
  });

  return (
    <form className={s.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => (
          <Field label={t('login.form.email')} span={12} required error={fieldState.error?.message}>
            {(props) => (
              <TextInput
                {...props}
                {...field}
                type="email"
                autoComplete="username"
                placeholder={t('login.form.email.placeholder')}
                disabled={bloqueado}
                // O contrato guarda o e-mail em minúsculas: normalizar ao digitar
                // evita transformar isso num erro de validação na cara do usuário.
                onChange={(e) => field.onChange(e.target.value.toLowerCase())}
              />
            )}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="senha"
        render={({ field, fieldState }) => (
          <Field
            label={t('login.form.password')}
            span={12}
            required
            error={fieldState.error?.message}
          >
            {(props) => (
              <span className={s.senhaWrap}>
                <TextInput
                  {...props}
                  {...field}
                  type={verSenha ? 'text' : 'password'}
                  autoComplete="current-password"
                  disabled={bloqueado}
                  className={[props.className, s.senhaInput].join(' ')}
                />
                <IconButton
                  className={s.verSenha}
                  icon={verSenha ? 'ban' : 'eye'}
                  label={t(verSenha ? 'login.form.password.hide' : 'login.form.password.show')}
                  size="sm"
                  onClick={() => setVerSenha((v) => !v)}
                />
              </span>
            )}
          </Field>
        )}
      />

      <div className={s.linhaAuxiliar}>
        <Controller
          control={control}
          name="lembrar"
          render={({ field }) => (
            <label className={s.check}>
              <input
                type="checkbox"
                name={field.name}
                ref={field.ref}
                checked={field.value}
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(e.target.checked)}
                disabled={bloqueado}
              />
              {t('login.form.remember')}
            </label>
          )}
        />
        {/* FR-109: a PoC não tem recuperação de senha própria. */}
        <span className={s.ajuda}>{t('login.form.forgot')}</span>
      </div>

      <LoadingButton
        type="submit"
        className={s.entrar}
        loading={pending}
        loadingLabel={t('login.form.submitting')}
        disabled={!formState.isValid || bloqueado}
      >
        {t('login.form.submit')}
      </LoadingButton>
    </form>
  );
}
